import React from 'react';
import { createFileRoute, useParams, Link } from '@tanstack/react-router';
import { TaskExecutionView } from '../../../../pages/tasks/TaskExecutionView';
import { useGetTask } from '../../../../api/tasks';
import { DEFAULT_TASK_TYPE } from '../../../../pages/tasks/utils';
import { LoadingState } from '../../../../components/common/LoadingState';
import { ErrorMessage } from '../../../../components/common/ErrorMessage';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

import { useTranslation } from 'react-i18next';

export const Route = createFileRoute(
  '/tasks/$taskId/execution/$conversationId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation('pleri/pages');
  const { taskId, conversationId } = useParams({
    from: '/tasks/$taskId/execution/$conversationId',
  });

  const {
    data: task,
    isLoading,
    error,
  } = useGetTask(DEFAULT_TASK_TYPE, taskId);

  if (isLoading) {
    return (
      <div className="flex-1 p-6">
        <LoadingState text={t('loadingExecution')} />
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="flex-1 p-6">
        <ErrorMessage
          message={(error as Error)?.message || t('failedToLoadTaskDetails')}
          onDismiss={() => {}}
        />
      </div>
    );
  }

  return (
    <div className="flex-1 bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
        <div className="mx-auto max-w-7xl px-6 py-6">
          {/* Breadcrumb */}
          <nav className="mb-4 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Link
              to="/tasks"
              className="transition-colors hover:text-orange-600"
            >
              {t('tasks')}
            </Link>
            <span>/</span>
            <Link
              to="/tasks/$taskId"
              params={{ taskId }}
              className="transition-colors hover:text-orange-600"
            >
              {task.name}
            </Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-gray-100">
              {t('execution')}
            </span>
          </nav>

          {/* Title and Actions */}
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <h1 className="text-3xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
                {t('executionDetails')}
              </h1>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {t('viewExecutionDetailsFor', { name: task.name })}
              </p>
            </div>

            <div className="ml-4">
              <Link
                to="/tasks/$taskId"
                params={{ taskId }}
                className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                {t('backToTask')}
              </Link>
            </div>
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="mx-auto max-w-7xl px-6 py-6">
        <TaskExecutionView taskId={taskId} conversationId={conversationId} />
      </div>
    </div>
  );
}
